import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  User,
  Save,
  MapPin,
  Plus,
  Edit,
  Trash2,
  Instagram,
  Twitter,
  Globe,
  Phone,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usersAPI, venuesAPI } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import { FieldError } from '../components/ui/FieldError';
import { isValidUrl, inputClass } from '../lib/validation';
import type { Venue, VenueFormData } from '../types';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// ============================================================
// Formulario de sede (dentro de un modal)
// ============================================================
function VenueForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: {
  initialData?: Venue;
  onSubmit: (data: VenueFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}) {
  const [form, setForm] = useState({
    name: initialData?.name || '',
    address: initialData?.address || '',
    notes: initialData?.notes || '',
  });
  const [nameError, setNameError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setNameError('El nombre de la sede es obligatorio');
      return;
    }
    onSubmit({
      name: form.name,
      address: form.address || undefined,
      notes: form.notes || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div>
        <label htmlFor="name" className="label">Nombre de la sede *</label>
        <input
          id="name"
          className={inputClass(!!nameError)}
          aria-invalid={!!nameError}
          value={form.name}
          onChange={(e) => {
            setForm((f) => ({ ...f, name: e.target.value }));
            if (nameError) setNameError('');
          }}
          placeholder="AMFG - Galería"
        />
        <FieldError message={nameError} />
      </div>
      <div>
        <label htmlFor="address" className="label">Dirección</label>
        <input
          id="address"
          className="input"
          value={form.address}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          placeholder="Av. Siempreviva 742"
        />
      </div>
      <div>
        <label htmlFor="notes" className="label">Notas</label>
        <textarea
          id="notes"
          className="input resize-none"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Horarios, accesos, observaciones..."
        />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
        <button type="button" onClick={onCancel} className="btn-secondary">
          <X className="w-4 h-4" />
          Cancelar
        </button>
        <button type="submit" disabled={isLoading} className="btn-primary">
          {isLoading ? <LoadingSpinner size="sm" /> : (<><Save className="w-4 h-4" />Guardar</>)}
        </button>
      </div>
    </form>
  );
}

// ============================================================
// Página Mi Perfil (entrenador)
// ============================================================
export function Profile() {
  const queryClient = useQueryClient();

  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersAPI.getMe(),
  });

  const { data: venues } = useQuery({
    queryKey: ['venues'],
    queryFn: () => venuesAPI.getAll(),
  });

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    bio: '',
    instagram: '',
    twitter: '',
    website: '',
  });

  // Sincronizar el form cuando llegan los datos del perfil.
  useEffect(() => {
    if (me) {
      setProfile({
        firstName: me.firstName || '',
        lastName: me.lastName || '',
        phone: me.phone || '',
        bio: me.bio || '',
        instagram: me.instagram || '',
        twitter: me.twitter || '',
        website: me.website || '',
      });
    }
  }, [me]);

  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [deletingVenue, setDeletingVenue] = useState<Venue | null>(null);

  const updateProfileMutation = useMutation({
    mutationFn: (data: typeof profile) => usersAPI.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('Perfil actualizado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const createVenueMutation = useMutation({
    mutationFn: (data: VenueFormData) => venuesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      setIsVenueModalOpen(false);
      toast.success('Sede creada');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateVenueMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: VenueFormData }) => venuesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      setEditingVenue(null);
      toast.success('Sede actualizada');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteVenueMutation = useMutation({
    mutationFn: (id: string) => venuesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      setDeletingVenue(null);
      toast.success('Sede eliminada');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!profile.firstName.trim()) errs.firstName = 'El nombre es obligatorio';
    if (!profile.lastName.trim()) errs.lastName = 'El apellido es obligatorio';
    if (profile.website && !isValidUrl(profile.website))
      errs.website = 'URL inválida (debe empezar con http:// o https://)';
    setProfileErrors(errs);
    if (Object.keys(errs).length > 0) return;
    updateProfileMutation.mutate(profile);
  };

  const handleVenueSubmit = (data: VenueFormData) => {
    if (editingVenue) {
      updateVenueMutation.mutate({ id: editingVenue.id, data });
    } else {
      createVenueMutation.mutate(data);
    }
  };

  const setField = (name: keyof typeof profile) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setProfile((p) => ({ ...p, [name]: e.target.value }));
    if (profileErrors[name]) setProfileErrors((prev) => ({ ...prev, [name]: '' }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={itemVariants}>
        <h1 className="section-title flex items-center gap-3">
          <User className="w-8 h-8 text-dark-100" />
          Mi Perfil
        </h1>
        <p className="section-subtitle">
          Tus datos y redes son visibles para tus alumnos desde la app.
        </p>
      </motion.div>

      {/* Datos del entrenador */}
      <motion.form variants={itemVariants} onSubmit={handleProfileSubmit} noValidate className="card space-y-4">
        <h2 className="text-lg font-semibold text-dark-50">Datos personales</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="label">Nombre *</label>
            <input
              id="firstName"
              className={inputClass(!!profileErrors.firstName)}
              aria-invalid={!!profileErrors.firstName}
              value={profile.firstName}
              onChange={setField('firstName')}
            />
            <FieldError message={profileErrors.firstName} />
          </div>
          <div>
            <label htmlFor="lastName" className="label">Apellido *</label>
            <input
              id="lastName"
              className={inputClass(!!profileErrors.lastName)}
              aria-invalid={!!profileErrors.lastName}
              value={profile.lastName}
              onChange={setField('lastName')}
            />
            <FieldError message={profileErrors.lastName} />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="label flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5" /> Teléfono
          </label>
          <input id="phone" className="input" value={profile.phone} onChange={setField('phone')} placeholder="+54 11 1234-5678" />
        </div>

        <div>
          <label htmlFor="bio" className="label">Bio</label>
          <textarea
            id="bio"
            className="input resize-none"
            rows={3}
            value={profile.bio}
            onChange={setField('bio')}
            placeholder="Contales a tus alumnos sobre vos, tu metodología, certificaciones..."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="instagram" className="label flex items-center gap-1.5">
              <Instagram className="w-3.5 h-3.5" /> Instagram
            </label>
            <input id="instagram" className="input" value={profile.instagram} onChange={setField('instagram')} placeholder="@usuario" />
          </div>
          <div>
            <label htmlFor="twitter" className="label flex items-center gap-1.5">
              <Twitter className="w-3.5 h-3.5" /> Twitter / X
            </label>
            <input id="twitter" className="input" value={profile.twitter} onChange={setField('twitter')} placeholder="@usuario" />
          </div>
        </div>

        <div>
          <label htmlFor="website" className="label flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" /> Sitio web
          </label>
          <input
            id="website"
            className={inputClass(!!profileErrors.website)}
            aria-invalid={!!profileErrors.website}
            value={profile.website}
            onChange={setField('website')}
            placeholder="https://misitio.com"
          />
          <FieldError message={profileErrors.website} />
        </div>

        <div className="flex justify-end pt-4 border-t border-dark-700">
          <button type="submit" disabled={updateProfileMutation.isPending} className="btn-primary">
            {updateProfileMutation.isPending ? <LoadingSpinner size="sm" /> : (<><Save className="w-4 h-4" />Guardar cambios</>)}
          </button>
        </div>
      </motion.form>

      {/* Sedes de entrenamiento */}
      <motion.div variants={itemVariants} className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-dark-50 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary-500" />
            Sedes de entrenamiento
          </h2>
          <button onClick={() => { setEditingVenue(null); setIsVenueModalOpen(true); }} className="btn-primary btn-sm">
            <Plus className="w-4 h-4" />
            Nueva Sede
          </button>
        </div>

        {venues && venues.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {venues.map((venue: Venue) => (
              <div key={venue.id} className="card-hover">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-dark-100 truncate">{venue.name}</h3>
                      {venue.address && <p className="text-sm text-dark-400 truncate">{venue.address}</p>}
                      {venue.notes && <p className="text-xs text-dark-500 mt-1 line-clamp-2">{venue.notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setEditingVenue(venue); setIsVenueModalOpen(true); }}
                      className="p-1.5 rounded-lg text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors"
                      title="Editar sede"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeletingVenue(venue)}
                      className="p-1.5 rounded-lg text-dark-400 hover:text-red-400 hover:bg-dark-700 transition-colors"
                      title="Eliminar sede"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <MapPin className="w-16 h-16 mx-auto text-dark-600 mb-4" />
            <h3 className="text-lg font-semibold text-dark-300 mb-2">No hay sedes cargadas</h3>
            <p className="text-dark-400 mb-6">
              Cargá tus lugares de entrenamiento para asignarlos a tus alumnos.
            </p>
            <button onClick={() => { setEditingVenue(null); setIsVenueModalOpen(true); }} className="btn-primary">
              <Plus className="w-5 h-5" />
              Crear Sede
            </button>
          </div>
        )}
      </motion.div>

      {/* Modal crear/editar sede */}
      <Modal
        isOpen={isVenueModalOpen || !!editingVenue}
        onClose={() => { setIsVenueModalOpen(false); setEditingVenue(null); }}
        title={editingVenue ? 'Editar Sede' : 'Nueva Sede'}
      >
        <VenueForm
          initialData={editingVenue || undefined}
          onSubmit={handleVenueSubmit}
          onCancel={() => { setIsVenueModalOpen(false); setEditingVenue(null); }}
          isLoading={createVenueMutation.isPending || updateVenueMutation.isPending}
        />
      </Modal>

      {/* Modal eliminar sede */}
      <Modal
        isOpen={!!deletingVenue}
        onClose={() => setDeletingVenue(null)}
        title="Eliminar Sede"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            ¿Eliminar la sede <span className="font-semibold text-dark-100">{deletingVenue?.name}</span>?
            Los alumnos que la tengan asignada quedarán sin sede.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeletingVenue(null)} className="btn-secondary">Cancelar</button>
            <button
              onClick={() => deletingVenue && deleteVenueMutation.mutate(deletingVenue.id)}
              disabled={deleteVenueMutation.isPending}
              className="btn-danger"
            >
              {deleteVenueMutation.isPending ? <LoadingSpinner size="sm" /> : (<><Trash2 className="w-4 h-4" />Eliminar</>)}
            </button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
